import { FlowStep, CustomerNationalIdData } from "../../interfaces/altair";
import { LoadingType } from "../../agents/types";
import {
  extractNationalIdFront,
  extractNationalIdBack,
  extractDrivingLicense,
  extractVehicleLicenseFront,
  extractVehicleLicenseBack,
  GeminiExtractDataResponse,
  GeminiExtractNationalIdBackResponse,
  GeminiExtractDrivingLicenseResponse,
  GeminiExtractVehicleLicenseFrontResponse,
  GeminiExtractVehicleLicenseBackResponse,
} from "../../services/nationalIdService";
import { ExtractedDataField } from "../extracted-data-confirmation";
import {
  systemUiMessage,
  USER_UPLOADED_NATIONAL_ID_FRONT,
  USER_UPLOADED_NATIONAL_ID_BACK,
  USER_UPLOADED_DRIVING_LICENSE,
  USER_UPLOADED_VEHICLE_LICENSE_FRONT,
  USER_UPLOADED_VEHICLE_LICENSE_BACK,
  VEHICLE_LICENSE_MAKE_MODEL_MISMATCH,
  PHOTO_VALIDATION_FAILED_PREFIX,
  getDocumentUploadEventForFlowStep,
} from "../../constants/ameenSignals";



interface CreateHandleImagesUploadedParams {
  /** Send message through manager (use this instead of client.send so message reaches active session) */
  send: (message: string | { text: string }, turnComplete?: boolean) => void;
  getFlowStep: () => FlowStep;
  setCustomerNationalIdData: (
    updater: (prev: CustomerNationalIdData) => CustomerNationalIdData
  ) => void;
  setFlowStep?: (step: FlowStep) => void;
  onShowExtractedDataConfirmation?: (
    data: ExtractedDataField[],
    title: string,
    documentType: string,
    onConfirm: (editedData?: ExtractedDataField[]) => void,
    previewImageSrc?: string
  ) => void;
  /** Show loading overlay during document extraction (optional) */
  showLoading?: (message?: string, type?: LoadingType) => void;
  /** Hide loading overlay (optional) */
  hideLoading?: () => void;
}

export const createHandleImagesUploaded =
  ({
    send,
    getFlowStep,
    setCustomerNationalIdData,
    setFlowStep,
    onShowExtractedDataConfirmation,
    showLoading,
    hideLoading,
  }: CreateHandleImagesUploadedParams) =>
    async (files: File[]) => {
      const flowStep = getFlowStep();
      if (!files || files.length === 0 || !send) return;

      const file = files[0];
      const reader = new FileReader();

      reader.onload = async (e) => {
        // Get full base64 string with data:image prefix
        const fullBase64 = e.target?.result as string;
        // Extract just the base64 part for storage
        const base64 = fullBase64.split(",")[1] || fullBase64;

        console.log(`📸 [Ameen] Image uploaded, current flowStep: ${flowStep}`);

        try {
          showLoading?.("جاري رفع المستند ومعالجة البيانات...", "document_upload");
          // Call backend API to extract data based on document type
          let extractedDataFront: GeminiExtractDataResponse | undefined;
          let extractedDataBack: GeminiExtractNationalIdBackResponse | undefined;
          let extractedDrivingLicenseData: GeminiExtractDrivingLicenseResponse | undefined;
          let extractedVehicleLicenseFrontData: GeminiExtractVehicleLicenseFrontResponse | undefined;
          let extractedVehicleLicenseBackData: GeminiExtractVehicleLicenseBackResponse | undefined;

          if (flowStep === "national_id_front") {
            console.log(`📤 [Ameen] Calling backend API to extract National ID front data...`);
            extractedDataFront = await extractNationalIdFront(fullBase64, file.name);
            console.log(`✅ [Ameen] Backend extraction completed for front:`, extractedDataFront);
          } else if (flowStep === "national_id_back") {
            console.log(`📤 [Ameen] Calling backend API to extract National ID back data...`);
            extractedDataBack = await extractNationalIdBack(fullBase64, file.name);
            console.log(`✅ [Ameen] Backend extraction completed for back:`, extractedDataBack);
          } else if (flowStep === "driving_license") {
            console.log(`📤 [Ameen] Calling backend API to extract Driving License data...`);
            extractedDrivingLicenseData = await extractDrivingLicense(fullBase64, file.name);
            console.log(`✅ [Ameen] Backend extraction completed for driving license:`, extractedDrivingLicenseData);

          } else if (flowStep === "vehicle_license_front") {
            console.log(`📤 [Ameen] Calling backend API to extract Vehicle License front data...`);
            extractedVehicleLicenseFrontData = await extractVehicleLicenseFront(fullBase64, file.name);
            console.log(`✅ [Ameen] Backend extraction completed for vehicle license front:`, extractedVehicleLicenseFrontData);

          } else if (flowStep === "vehicle_license_back") {
            console.log(`📤 [Ameen] Calling backend API to extract Vehicle License back data...`);
            let statedMake: string | undefined;
            let statedModel: string | undefined;
            try {
              const raw = sessionStorage.getItem("saaed_user_data");
              if (raw) {
                const parsed = JSON.parse(raw);
                statedMake = parsed?.vehicleMakeStated || undefined;
                statedModel = parsed?.vehicleModelStated || undefined;
              }
            } catch { /* ignore */ }
            extractedVehicleLicenseBackData = await extractVehicleLicenseBack(fullBase64, file.name, statedMake, statedModel);
            console.log(`✅ [Ameen] Backend extraction completed for vehicle license back:`, extractedVehicleLicenseBackData);
          }

          // Update state with extracted data and image
          setCustomerNationalIdData((prev) => {
            const updated = flowStep === "national_id_front" && extractedDataFront
              ? {
                ...prev,
                national_id_front_image_base64: base64,
                // Update with extracted data from backend (front side)
                full_name: extractedDataFront.fullName || prev.full_name,
                address: extractedDataFront.address || prev.address,
                national_id_number: extractedDataFront.nationalIdNumber || prev.national_id_number,
                card_serial_number: extractedDataFront.cardSerialNumber || prev.card_serial_number,
                birth_date: extractedDataFront.birthDate || prev.birth_date,
              }
              : flowStep === "national_id_back" && extractedDataBack
                ? {
                  ...prev,
                  national_id_back_image_base64: base64,
                  // Update with extracted data from backend (back side)
                  national_id_number: extractedDataBack.nationaIdNumber || prev.national_id_number,
                  gender: extractedDataBack.gender || prev.gender,
                  religion: extractedDataBack.religion || prev.religion,
                  marital_status: extractedDataBack.maritalStatus || prev.marital_status,
                  profession: extractedDataBack.professionQualification || prev.profession,
                  expiration_date: extractedDataBack.dateOfExpiry || prev.expiration_date,
                  issue_batch_date: extractedDataBack.dateOfIssue || prev.issue_batch_date,
                }
                : flowStep === "national_id_front"
                  ? {
                    ...prev,
                    national_id_front_image_base64: base64,
                  }
                  : flowStep === "national_id_back"
                    ? {
                      ...prev,
                      national_id_back_image_base64: base64,
                    }
                    : prev;

            // Save to session storage when both images are uploaded
            // Only save text data, not base64 images (to avoid quota exceeded error)
            if (
              flowStep === "national_id_back" &&
              updated.national_id_front_image_base64 &&
              updated.national_id_back_image_base64
            ) {
              try {
                const existingData = sessionStorage.getItem("saaed_user_data");
                const userData = existingData ? JSON.parse(existingData) : {};

                // Extract only text fields (exclude base64 images to avoid storage quota issues)
                const textOnlyData = {
                  national_id_number: updated.national_id_number,
                  profession: updated.profession,
                  gender: updated.gender,
                  religion: updated.religion,
                  marital_status: updated.marital_status,
                  expiration_date: updated.expiration_date,
                  issue_batch_date: updated.issue_batch_date,
                  full_name: updated.full_name,
                  address: updated.address,
                  birth_date: updated.birth_date,
                  card_serial_number: updated.card_serial_number,
                  // Note: Images are stored in component state, not in sessionStorage
                  // to avoid QuotaExceededError
                };

                // Merge customer national ID data into user data
                const updatedUserData = {
                  ...userData,
                  customerNationalIdData: textOnlyData,
                };

                sessionStorage.setItem(
                  "saaed_user_data",
                  JSON.stringify(updatedUserData)
                );
                console.log(
                  "✅ [Ameen] Customer National ID data (text only) saved to session storage"
                );
              } catch (error) {
                console.error(
                  "❌ [Ameen] Failed to save to session storage:",
                  error
                );
              }
            }

            return updated;
          });

          // Save vehicle license front data to session storage when front is uploaded
          if (flowStep === "vehicle_license_front" && extractedVehicleLicenseFrontData) {
            try {
              const existingData = sessionStorage.getItem("saaed_user_data");
              const userData = existingData ? JSON.parse(existingData) : {};

              // Save front side data temporarily
              const vehicleLicenseFrontData = {
                issuingTrafficDepartment: extractedVehicleLicenseFrontData.issuingTrafficDepartment || "",
                issuingTrafficUnit: extractedVehicleLicenseFrontData.issuingTrafficUnit || "",
                ownerName: extractedVehicleLicenseFrontData.ownerName || "",
                ownerAddress: extractedVehicleLicenseFrontData.ownerAddress || "",
                ownerNationality: extractedVehicleLicenseFrontData.ownerNationality || "",
                licenseType: extractedVehicleLicenseFrontData.licenseType || "",
                licenseIssueDate: extractedVehicleLicenseFrontData.licenseIssueDate || "",
                licenseExpiryDate: extractedVehicleLicenseFrontData.licenseExpiryDate || "",
                vehiclePlateNumber: extractedVehicleLicenseFrontData.vehiclePlateNumber || "",
              };

              const updatedUserData = {
                ...userData,
                customerVehicleLicenseFrontData: vehicleLicenseFrontData,
              };

              sessionStorage.setItem(
                "saaed_user_data",
                JSON.stringify(updatedUserData)
              );
              console.log(
                "✅ [Ameen] Customer Vehicle License front data saved to session storage"
              );
            } catch (error) {
              console.error(
                "❌ [Ameen] Failed to save vehicle license front data to session storage:",
                error
              );
            }
          }

          // Save complete vehicle license data to session storage when both sides are uploaded
          if (flowStep === "vehicle_license_back" && extractedVehicleLicenseBackData) {
            try {
              const existingData = sessionStorage.getItem("saaed_user_data");
              const userData = existingData ? JSON.parse(existingData) : {};

              // Get front data from session storage (saved when front was uploaded)
              const frontData = userData.customerVehicleLicenseFrontData || {};

              // Extract only text fields (exclude base64 images to avoid storage quota issues)
              const vehicleLicenseData = {
                // Front side data (from session storage)
                issuingTrafficDepartment: frontData.issuingTrafficDepartment || "",
                issuingTrafficUnit: frontData.issuingTrafficUnit || "",
                ownerName: frontData.ownerName || "",
                ownerAddress: frontData.ownerAddress || "",
                ownerNationality: frontData.ownerNationality || "",
                licenseType: frontData.licenseType || "",
                licenseIssueDate: frontData.licenseIssueDate || "",
                licenseExpiryDate: frontData.licenseExpiryDate || "",
                vehiclePlateNumber: frontData.vehiclePlateNumber || "",
                // Back side data (from current extraction)
                make: extractedVehicleLicenseBackData.make || "",
                model: extractedVehicleLicenseBackData.model || "",
                chassisNumber: extractedVehicleLicenseBackData.chassisNumber || "",
                motorNumber: extractedVehicleLicenseBackData.motorNumber || "",
                engineCylinders: extractedVehicleLicenseBackData.engineCylinders || "",
                fuelType: extractedVehicleLicenseBackData.fuelType || "",
                engineCapacity: extractedVehicleLicenseBackData.engineCapacity || "",
                features: extractedVehicleLicenseBackData.features || "",
                code: extractedVehicleLicenseBackData.code || "",
                modelYear: extractedVehicleLicenseBackData.modelYear || "",
                bodyStyle: extractedVehicleLicenseBackData.bodyStyle || "",
                inspectionExpiryYear: extractedVehicleLicenseBackData.inspectionExpiryYear || "",
                vehicleColor: extractedVehicleLicenseBackData.vehicleColor || "",
                // Note: Images are stored in component state, not in sessionStorage
                // to avoid QuotaExceededError
              };

              // Merge complete vehicle license data into user data
              const updatedUserData = {
                ...userData,
                customerVehicleLicenseData: vehicleLicenseData,
                // Remove temporary front data
                customerVehicleLicenseFrontData: undefined,
              };

              sessionStorage.setItem(
                "saaed_user_data",
                JSON.stringify(updatedUserData)
              );
              console.log(
                "✅ [Ameen] Customer Vehicle License data (text only) saved to session storage"
              );
            } catch (error) {
              console.error(
                "❌ [Ameen] Failed to save vehicle license data to session storage:",
                error
              );
            }
          }

          // Helper function to save extracted data to session storage (same as UI updates)
          const saveExtractedDataToSession = (data: any, flowStep: FlowStep) => {
            try {
              const existingData = sessionStorage.getItem("saaed_user_data");
              const userData = existingData ? JSON.parse(existingData) : {};

              if (flowStep === "national_id_front" && data) {
                const textOnlyData = {
                  full_name: data.fullName || "",
                  address: data.address || "",
                  national_id_number: data.nationalIdNumber || "",
                  card_serial_number: data.cardSerialNumber || "",
                  birth_date: data.birthDate || "",
                };
                const updatedUserData = {
                  ...userData,
                  customerNationalIdData: {
                    ...(userData.customerNationalIdData || {}),
                    ...textOnlyData,
                  },
                };
                sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
                console.log("✅ [Ameen] Saved National ID front data to session storage");
              } else if (flowStep === "national_id_back" && data) {
                const textOnlyData = {
                  national_id_number: data.nationaIdNumber || "",
                  gender: data.gender || "",
                  religion: data.religion || "",
                  marital_status: data.maritalStatus || "",
                  profession: data.professionQualification || "",
                  expiration_date: data.dateOfExpiry || "",
                  issue_batch_date: data.dateOfIssue || "",
                };
                const updatedUserData = {
                  ...userData,
                  customerNationalIdData: {
                    ...(userData.customerNationalIdData || {}),
                    ...textOnlyData,
                  },
                };
                sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
                console.log("✅ [Ameen] Saved National ID back data to session storage");
              } else if (flowStep === "driving_license" && data) {
                const drivingLicenseData = {
                  issuingAuthority: data.issuingAuthority || "",
                  issuingOffice: data.issuingOffice || "",
                  licenseType: data.licenseType || "",
                  nationalId: data.nationalId || "",
                  nameArabic: data.nameArabic || "",
                  nameEnglish: data.nameEnglish || "",
                  address: data.address || "",
                  nationalityArabic: data.nationalityArabic || "",
                  nationalityEnglish: data.nationalityEnglish || "",
                  occupation: data.occupation || "",
                  dateOfIssue: data.dateOfIssue || "",
                  dateOfExpiry: data.dateOfExpiry || "",
                  licenseClass: data.licenseClass || "",
                };
                const updatedUserData = {
                  ...userData,
                  customerDrivingLicenseData: drivingLicenseData,
                };
                sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
                console.log("✅ [Ameen] Saved Driving License data to session storage");
              } else if (flowStep === "vehicle_license_front" && data) {
                const vehicleLicenseFrontData = {
                  issuingTrafficDepartment: data.issuingTrafficDepartment || "",
                  issuingTrafficUnit: data.issuingTrafficUnit || "",
                  ownerName: data.ownerName || "",
                  ownerAddress: data.ownerAddress || "",
                  ownerNationality: data.ownerNationality || "",
                  licenseType: data.licenseType || "",
                  licenseIssueDate: data.licenseIssueDate || "",
                  licenseExpiryDate: data.licenseExpiryDate || "",
                  vehiclePlateNumber: data.vehiclePlateNumber || "",
                };
                const updatedUserData = {
                  ...userData,
                  customerVehicleLicenseFrontData: vehicleLicenseFrontData,
                };
                sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
                console.log("✅ [Ameen] Saved Vehicle License front data to session storage");
              } else if (flowStep === "vehicle_license_back" && data) {
                const frontData = userData.customerVehicleLicenseFrontData || {};
                const vehicleLicenseData = {
                  ...frontData,
                  make: data.make || "",
                  model: data.model || "",
                  chassisNumber: data.chassisNumber || "",
                  motorNumber: data.motorNumber || "",
                  engineCylinders: data.engineCylinders || "",
                  fuelType: data.fuelType || "",
                  engineCapacity: data.engineCapacity || "",
                  features: data.features || "",
                  code: data.code || "",
                  modelYear: data.modelYear || "",
                  bodyStyle: data.bodyStyle || "",
                  inspectionExpiryYear: data.inspectionExpiryYear || "",
                  vehicleColor: data.vehicleColor || "",
                };
                const updatedUserData = {
                  ...userData,
                  customerVehicleLicenseData: vehicleLicenseData,
                  customerVehicleLicenseFrontData: undefined,
                };
                sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
                console.log("✅ [Ameen] Saved Vehicle License back data to session storage");
              }
            } catch (error) {
              console.error("❌ [Ameen] Failed to save extracted data to session storage:", error);
            }
          };

          // Helper function to convert edited data fields back to data object
          const convertEditedFieldsToData = (
            editedFields: ExtractedDataField[] | undefined,
            originalData: any,
            flowStep: FlowStep
          ): any => {
            if (!editedFields || editedFields.length === 0) {
              return originalData;
            }

            // Create a map of label to value for quick lookup
            const fieldMap = new Map<string, string>();
            editedFields.forEach(field => {
              fieldMap.set(field.label, field.value || "");
            });

            // Convert based on flowStep
            if (flowStep === "national_id_front") {
              return {
                ...originalData,
                fullName: fieldMap.get("الاسم الكامل") || originalData.fullName || "",
                address: fieldMap.get("العنوان") || originalData.address || "",
                nationalIdNumber: fieldMap.get("رقم الرقم القومي") || originalData.nationalIdNumber || "",
                cardSerialNumber: fieldMap.get("رقم البطاقة") || originalData.cardSerialNumber || "",
                birthDate: fieldMap.get("تاريخ الميلاد") || originalData.birthDate || "",
              };
            } else if (flowStep === "national_id_back") {
              return {
                ...originalData,
                nationaIdNumber: fieldMap.get("رقم الرقم القومي") || originalData.nationaIdNumber || "",
                gender: fieldMap.get("الجنس") || originalData.gender || "",
                religion: fieldMap.get("الدين") || originalData.religion || "",
                maritalStatus: fieldMap.get("الحالة الاجتماعية") || originalData.maritalStatus || "",
                professionQualification: fieldMap.get("المهنة") || originalData.professionQualification || "",
                dateOfIssue: fieldMap.get("تاريخ الإصدار") || originalData.dateOfIssue || "",
                dateOfExpiry: fieldMap.get("تاريخ الانتهاء") || originalData.dateOfExpiry || "",
              };
            } else if (flowStep === "driving_license") {
              return {
                ...originalData,
                issuingAuthority: fieldMap.get("السلطة المصدرة") || originalData.issuingAuthority || "",
                issuingOffice: fieldMap.get("المكتب المصدر") || originalData.issuingOffice || "",
                licenseType: fieldMap.get("نوع الرخصة") || originalData.licenseType || "",
                nationalId: fieldMap.get("الرقم القومي") || originalData.nationalId || "",
                nameArabic: fieldMap.get("الاسم (عربي)") || originalData.nameArabic || "",
                nameEnglish: fieldMap.get("الاسم (إنجليزي)") || originalData.nameEnglish || "",
                address: fieldMap.get("العنوان") || originalData.address || "",
                nationalityArabic: fieldMap.get("الجنسية (عربي)") || originalData.nationalityArabic || "",
                nationalityEnglish: fieldMap.get("الجنسية (إنجليزي)") || originalData.nationalityEnglish || "",
                occupation: fieldMap.get("المهنة") || originalData.occupation || "",
                dateOfIssue: fieldMap.get("تاريخ الإصدار") || originalData.dateOfIssue || "",
                dateOfExpiry: fieldMap.get("تاريخ الانتهاء") || originalData.dateOfExpiry || "",
                licenseClass: fieldMap.get("فئة الرخصة") || originalData.licenseClass || "",
              };
            } else if (flowStep === "vehicle_license_front") {
              return {
                ...originalData,
                issuingTrafficDepartment: fieldMap.get("إدارة المرور المصدرة") || originalData.issuingTrafficDepartment || "",
                issuingTrafficUnit: fieldMap.get("وحدة المرور المصدرة") || originalData.issuingTrafficUnit || "",
                ownerName: fieldMap.get("اسم المالك") || originalData.ownerName || "",
                ownerAddress: fieldMap.get("عنوان المالك") || originalData.ownerAddress || "",
                ownerNationality: fieldMap.get("جنسية المالك") || originalData.ownerNationality || "",
                licenseType: fieldMap.get("نوع الرخصة") || originalData.licenseType || "",
                licenseIssueDate: fieldMap.get("تاريخ الإصدار") || originalData.licenseIssueDate || "",
                licenseExpiryDate: fieldMap.get("تاريخ الانتهاء") || originalData.licenseExpiryDate || "",
                vehiclePlateNumber: fieldMap.get("رقم اللوحة") || originalData.vehiclePlateNumber || "",
              };
            } else if (flowStep === "vehicle_license_back") {
              return {
                ...originalData,
                make: fieldMap.get("الماركة") || originalData.make || "",
                model: fieldMap.get("الموديل") || originalData.model || "",
                chassisNumber: fieldMap.get("رقم الشاصي") || originalData.chassisNumber || "",
                motorNumber: fieldMap.get("رقم المحرك") || originalData.motorNumber || "",
                engineCylinders: fieldMap.get("عدد أسطوانات المحرك") || originalData.engineCylinders || "",
                fuelType: fieldMap.get("نوع الوقود") || originalData.fuelType || "",
                engineCapacity: fieldMap.get("سعة المحرك") || originalData.engineCapacity || "",
                features: fieldMap.get("المميزات") || originalData.features || "",
                code: fieldMap.get("الكود") || originalData.code || "",
                modelYear: fieldMap.get("سنة الصنع") || originalData.modelYear || "",
                bodyStyle: fieldMap.get("نوع الهيكل") || originalData.bodyStyle || "",
                inspectionExpiryYear: fieldMap.get("سنة انتهاء الفحص") || originalData.inspectionExpiryYear || "",
                vehicleColor: fieldMap.get("لون السيارة") || originalData.vehicleColor || "",
              };
            }

            return originalData;
          };

          // Helper function to create extracted data string from data object
          const createExtractedDataString = (data: any, flowStep: FlowStep): string => {
            if (flowStep === "national_id_front") {
              return `"Full Name": "${data.fullName || ""}", "Address": "${data.address || ""}", "National ID Number": "${data.nationalIdNumber || ""}", "Card Serial Number": "${data.cardSerialNumber || ""}", "Birth Date": "${data.birthDate || ""}"`;
            } else if (flowStep === "national_id_back") {
              return `"National ID Number": "${data.nationaIdNumber || ""}", "Gender": "${data.gender || ""}", "Religion": "${data.religion || ""}", "Marital Status": "${data.maritalStatus || ""}", "Profession": "${data.professionQualification || ""}", "Date of Issue": "${data.dateOfIssue || ""}", "Date of Expiry": "${data.dateOfExpiry || ""}"`;
            } else if (flowStep === "driving_license") {
              return `"Issuing Authority": "${data.issuingAuthority || ""}", "Issuing Office": "${data.issuingOffice || ""}", "License Type": "${data.licenseType || ""}", "National ID": "${data.nationalId || ""}", "Name (Arabic)": "${data.nameArabic || ""}", "Name (English)": "${data.nameEnglish || ""}", "Address": "${data.address || ""}", "Nationality (Arabic)": "${data.nationalityArabic || ""}", "Nationality (English)": "${data.nationalityEnglish || ""}", "Occupation": "${data.occupation || ""}", "Date of Issue": "${data.dateOfIssue || ""}", "Date of Expiry": "${data.dateOfExpiry || ""}", "License Class": "${data.licenseClass || ""}"`;
            } else if (flowStep === "vehicle_license_front") {
              return `"Issuing Traffic Department": "${data.issuingTrafficDepartment || ""}", "Issuing Traffic Unit": "${data.issuingTrafficUnit || ""}", "Owner Name": "${data.ownerName || ""}", "Owner Address": "${data.ownerAddress || ""}", "Owner Nationality": "${data.ownerNationality || ""}", "License Type": "${data.licenseType || ""}", "License Issue Date": "${data.licenseIssueDate || ""}", "License Expiry Date": "${data.licenseExpiryDate || ""}", "Vehicle Plate Number": "${data.vehiclePlateNumber || ""}"`;
            } else if (flowStep === "vehicle_license_back") {
              return `"Make": "${data.make || ""}", "Model": "${data.model || ""}", "Chassis Number": "${data.chassisNumber || ""}", "Motor Number": "${data.motorNumber || ""}", "Engine Cylinders": "${data.engineCylinders || ""}", "Fuel Type": "${data.fuelType || ""}", "Engine Capacity": "${data.engineCapacity || ""}", "Features": "${data.features || ""}", "Code": "${data.code || ""}", "Model Year": "${data.modelYear || ""}", "Body Style": "${data.bodyStyle || ""}", "Inspection Expiry Year": "${data.inspectionExpiryYear || ""}", "Vehicle Color": "${data.vehicleColor || ""}"`;
            }
            return "";
          };

          // Sanitize and truncate extracted data to avoid 1011 / internal error (API limits or invalid chars)
          const MAX_EXTRACTED_DATA_LENGTH = 1500;
          const MAX_VEHICLE_LICENSE_BACK_LENGTH = 800; // Shorter for transition path to avoid 1011
          const sanitizeForSend = (s: string, maxLen?: number): string => {
            const limit = maxLen ?? MAX_EXTRACTED_DATA_LENGTH;
            const noControl = s.replace(/[\x00-\x1f\x7f]/g, " ").trim();
            return noControl.length > limit ? noControl.slice(0, limit) + "..." : noControl;
          };
          const normalizeForCompare = (value?: string): string =>
            (value || "").trim().toLowerCase();
          const getVehicleMakeModelMismatchPayload = (
            licenseMake: string,
            licenseModel: string
          ): string | null => {
            try {
              const raw = sessionStorage.getItem("saaed_user_data");
              const parsed = raw ? JSON.parse(raw) : {};
              const userMake = (parsed?.vehicleMakeStated || "").trim();
              const userModel = (parsed?.vehicleModelStated || "").trim();
              const extractedMake = (licenseMake || "").trim();
              const extractedModel = (licenseModel || "").trim();

              if (!userMake && !userModel) {
                return null;
              }

              const isMakeMatch =
                normalizeForCompare(userMake) === normalizeForCompare(extractedMake);
              const isModelMatch =
                normalizeForCompare(userModel) === normalizeForCompare(extractedModel);

              if (isMakeMatch && isModelMatch) {
                return null;
              }

              return [
                `user_entered_brand="${userMake || "-"}"`,
                `user_entered_model="${userModel || "-"}"`,
                `license_brand="${extractedMake || "-"}"`,
                `license_model="${extractedModel || "-"}"`,
              ].join(", ");
            } catch {
              return null;
            }
          };

          // Helper function to send system_ui message and proceed
          const proceedWithExtractedData = (
            extractedDataString: string,
            systemUiEvent: string
          ) => {
            const safeData = sanitizeForSend(extractedDataString);
            console.log(`📤 [Ameen] Sending [system_ui] ${systemUiEvent} with extracted data`);

            // Vehicle license back: send ONE message with turnComplete true + shorter payload to avoid 1011
            const isVehicleLicenseBack = systemUiEvent === USER_UPLOADED_VEHICLE_LICENSE_BACK;
            if (isVehicleLicenseBack) {
              const shortData = sanitizeForSend(extractedDataString, MAX_VEHICLE_LICENSE_BACK_LENGTH);
              send(
                { text: systemUiMessage(systemUiEvent, shortData) },
                true
              );
              if (setFlowStep) setFlowStep("idle");
              return;
            }

            send(
              { text: systemUiMessage(systemUiEvent, safeData) },
              true
            );

            if (setFlowStep) {
              setFlowStep("idle");
            }
          };

          // Show confirmation modal for extracted data before proceeding
          if (flowStep === "national_id_front" && extractedDataFront) {
            const extractedDataFields: ExtractedDataField[] = [
              { label: "الاسم الكامل", value: extractedDataFront.fullName || "" },
              { label: "العنوان", value: extractedDataFront.address || "" },
              { label: "رقم الرقم القومي", value: extractedDataFront.nationalIdNumber || "" },
              { label: "رقم البطاقة", value: extractedDataFront.cardSerialNumber || "" },
              { label: "تاريخ الميلاد", value: extractedDataFront.birthDate || "" },
            ];
            const extractedDataString = `"Full Name": "${extractedDataFront.fullName || ""}", "Address": "${extractedDataFront.address || ""}", "National ID Number": "${extractedDataFront.nationalIdNumber || ""}", "Card Serial Number": "${extractedDataFront.cardSerialNumber || ""}", "Birth Date": "${extractedDataFront.birthDate || ""}"`;

            if (onShowExtractedDataConfirmation) {
              onShowExtractedDataConfirmation(
                extractedDataFields,
                "تأكيد بيانات بطاقة الرقم القومي - الوجه الأمامي",
                "بطاقة الرقم القومي - الوجه الأمامي",
                (editedData) => {
                  const finalData = convertEditedFieldsToData(editedData, extractedDataFront, "national_id_front");
                  const dataString = createExtractedDataString(finalData, "national_id_front");
                  // Update state with edited data
                  setCustomerNationalIdData((prev) => ({
                    ...prev,
                    full_name: finalData.fullName || prev.full_name,
                    address: finalData.address || prev.address,
                    national_id_number: finalData.nationalIdNumber || prev.national_id_number,
                    card_serial_number: finalData.cardSerialNumber || prev.card_serial_number,
                    birth_date: finalData.birthDate || prev.birth_date,
                  }));
                  // Persist confirmed front national ID into session user payload
                  try {
                    const raw = sessionStorage.getItem("saaed_user_data");
                    const userData = raw ? JSON.parse(raw) : {};
                    userData.customerNationalIdData = {
                      ...(userData.customerNationalIdData || {}),
                      national_id_number: (finalData.nationalIdNumber || "").trim(),
                    };
                    sessionStorage.setItem("saaed_user_data", JSON.stringify(userData));
                    console.log("✅ [Ameen] Saved front national ID number to session storage");
                  } catch (e) {
                    console.warn("⚠️ [Ameen] Failed to persist front national ID to session storage", e);
                  }
                  // ───────────────────────────────────────────────────────────────────────────────
                  proceedWithExtractedData(dataString, USER_UPLOADED_NATIONAL_ID_FRONT);
                },
                fullBase64
              );
            } else {
              // Fallback: proceed immediately if no confirmation callback
              proceedWithExtractedData(extractedDataString, USER_UPLOADED_NATIONAL_ID_FRONT);
            }
          } else if (flowStep === "national_id_back" && extractedDataBack) {
            const extractedDataFields: ExtractedDataField[] = [
              { label: "رقم الرقم القومي", value: extractedDataBack.nationaIdNumber || "" },
              { label: "الجنس", value: extractedDataBack.gender || "" },
              { label: "الدين", value: extractedDataBack.religion || "" },
              { label: "الحالة الاجتماعية", value: extractedDataBack.maritalStatus || "" },
              { label: "المهنة", value: extractedDataBack.professionQualification || "" },
              { label: "تاريخ الإصدار", value: extractedDataBack.dateOfIssue || "" },
              { label: "تاريخ الانتهاء", value: extractedDataBack.dateOfExpiry || "" },
            ];
            const extractedDataString = `"National ID Number": "${extractedDataBack.nationaIdNumber || ""}", "Gender": "${extractedDataBack.gender || ""}", "Religion": "${extractedDataBack.religion || ""}", "Marital Status": "${extractedDataBack.maritalStatus || ""}", "Profession": "${extractedDataBack.professionQualification || ""}", "Date of Issue": "${extractedDataBack.dateOfIssue || ""}", "Date of Expiry": "${extractedDataBack.dateOfExpiry || ""}"`;

            if (onShowExtractedDataConfirmation) {
              onShowExtractedDataConfirmation(
                extractedDataFields,
                "تأكيد بيانات بطاقة الرقم القومي - الوجه الخلفي",
                "بطاقة الرقم القومي - الوجه الخلفي",
                (editedData) => {
                  const finalData = convertEditedFieldsToData(editedData, extractedDataBack, "national_id_back");
                  const dataString = createExtractedDataString(finalData, "national_id_back");
                  // Update state with edited data
                  setCustomerNationalIdData((prev) => ({
                    ...prev,
                    national_id_number: finalData.nationaIdNumber || prev.national_id_number,
                    gender: finalData.gender || prev.gender,
                    religion: finalData.religion || prev.religion,
                    marital_status: finalData.maritalStatus || prev.marital_status,
                    profession: finalData.professionQualification || prev.profession,
                    expiration_date: finalData.dateOfExpiry || prev.expiration_date,
                    issue_batch_date: finalData.dateOfIssue || prev.issue_batch_date,
                  }));
                  proceedWithExtractedData(dataString, USER_UPLOADED_NATIONAL_ID_BACK);
                },
                fullBase64
              );
            } else {
              proceedWithExtractedData(extractedDataString, USER_UPLOADED_NATIONAL_ID_BACK);
            }
          } else if (flowStep === "driving_license" && extractedDrivingLicenseData) {
            const extractedDataFields: ExtractedDataField[] = [
              { label: "السلطة المصدرة", value: extractedDrivingLicenseData.issuingAuthority || "" },
              { label: "المكتب المصدر", value: extractedDrivingLicenseData.issuingOffice || "" },
              { label: "نوع الرخصة", value: extractedDrivingLicenseData.licenseType || "" },
              { label: "الرقم القومي", value: extractedDrivingLicenseData.nationalId || "" },
              { label: "الاسم (عربي)", value: extractedDrivingLicenseData.nameArabic || "" },
              { label: "الاسم (إنجليزي)", value: extractedDrivingLicenseData.nameEnglish || "" },
              { label: "العنوان", value: extractedDrivingLicenseData.address || "" },
              { label: "الجنسية (عربي)", value: extractedDrivingLicenseData.nationalityArabic || "" },
              { label: "الجنسية (إنجليزي)", value: extractedDrivingLicenseData.nationalityEnglish || "" },
              { label: "المهنة", value: extractedDrivingLicenseData.occupation || "" },
              { label: "تاريخ الإصدار", value: extractedDrivingLicenseData.dateOfIssue || "" },
              { label: "تاريخ الانتهاء", value: extractedDrivingLicenseData.dateOfExpiry || "" },
              { label: "فئة الرخصة", value: extractedDrivingLicenseData.licenseClass || "" },
            ];
            const extractedDataString = `"Issuing Authority": "${extractedDrivingLicenseData.issuingAuthority || ""}", "Issuing Office": "${extractedDrivingLicenseData.issuingOffice || ""}", "License Type": "${extractedDrivingLicenseData.licenseType || ""}", "National ID": "${extractedDrivingLicenseData.nationalId || ""}", "Name (Arabic)": "${extractedDrivingLicenseData.nameArabic || ""}", "Name (English)": "${extractedDrivingLicenseData.nameEnglish || ""}", "Address": "${extractedDrivingLicenseData.address || ""}", "Nationality (Arabic)": "${extractedDrivingLicenseData.nationalityArabic || ""}", "Nationality (English)": "${extractedDrivingLicenseData.nationalityEnglish || ""}", "Occupation": "${extractedDrivingLicenseData.occupation || ""}", "Date of Issue": "${extractedDrivingLicenseData.dateOfIssue || ""}", "Date of Expiry": "${extractedDrivingLicenseData.dateOfExpiry || ""}", "License Class": "${extractedDrivingLicenseData.licenseClass || ""}"`;

            if (onShowExtractedDataConfirmation) {
              onShowExtractedDataConfirmation(
                extractedDataFields,
                "تأكيد بيانات رخصة القيادة",
                "رخصة القيادة",
                (editedData) => {
                  const finalData = convertEditedFieldsToData(editedData, extractedDrivingLicenseData, "driving_license");
                  const dataString = createExtractedDataString(finalData, "driving_license");
                  proceedWithExtractedData(dataString, USER_UPLOADED_DRIVING_LICENSE);
                },
                fullBase64
              );
            } else {
              proceedWithExtractedData(extractedDataString, USER_UPLOADED_DRIVING_LICENSE);
            }
          } else if (flowStep === "vehicle_license_front" && extractedVehicleLicenseFrontData) {
            const extractedDataFields: ExtractedDataField[] = [
              { label: "إدارة المرور المصدرة", value: extractedVehicleLicenseFrontData.issuingTrafficDepartment || "" },
              { label: "وحدة المرور المصدرة", value: extractedVehicleLicenseFrontData.issuingTrafficUnit || "" },
              { label: "اسم المالك", value: extractedVehicleLicenseFrontData.ownerName || "" },
              { label: "عنوان المالك", value: extractedVehicleLicenseFrontData.ownerAddress || "" },
              { label: "جنسية المالك", value: extractedVehicleLicenseFrontData.ownerNationality || "" },
              { label: "نوع الرخصة", value: extractedVehicleLicenseFrontData.licenseType || "" },
              { label: "تاريخ الإصدار", value: extractedVehicleLicenseFrontData.licenseIssueDate || "" },
              { label: "تاريخ الانتهاء", value: extractedVehicleLicenseFrontData.licenseExpiryDate || "" },
              { label: "رقم اللوحة", value: extractedVehicleLicenseFrontData.vehiclePlateNumber || "" },
            ];
            const extractedDataString = `"Issuing Traffic Department": "${extractedVehicleLicenseFrontData.issuingTrafficDepartment || ""}", "Issuing Traffic Unit": "${extractedVehicleLicenseFrontData.issuingTrafficUnit || ""}", "Owner Name": "${extractedVehicleLicenseFrontData.ownerName || ""}", "Owner Address": "${extractedVehicleLicenseFrontData.ownerAddress || ""}", "Owner Nationality": "${extractedVehicleLicenseFrontData.ownerNationality || ""}", "License Type": "${extractedVehicleLicenseFrontData.licenseType || ""}", "License Issue Date": "${extractedVehicleLicenseFrontData.licenseIssueDate || ""}", "License Expiry Date": "${extractedVehicleLicenseFrontData.licenseExpiryDate || ""}", "Vehicle Plate Number": "${extractedVehicleLicenseFrontData.vehiclePlateNumber || ""}"`;

            if (onShowExtractedDataConfirmation) {
              onShowExtractedDataConfirmation(
                extractedDataFields,
                "تأكيد بيانات رخصة السيارة - الوجه الأمامي",
                "رخصة السيارة - الوجه الأمامي",
                (editedData) => {
                  const finalData = convertEditedFieldsToData(editedData, extractedVehicleLicenseFrontData, "vehicle_license_front");
                  const dataString = createExtractedDataString(finalData, "vehicle_license_front");
                  proceedWithExtractedData(dataString, "user_uploaded_vehicle_license_front");
                },
                fullBase64
              );
            } else {
              proceedWithExtractedData(extractedDataString, USER_UPLOADED_VEHICLE_LICENSE_FRONT);
            }
          } else if (flowStep === "vehicle_license_back" && extractedVehicleLicenseBackData) {
            const extractedDataFields: ExtractedDataField[] = [
              { label: "الماركة", value: extractedVehicleLicenseBackData.make || "" },
              { label: "الموديل", value: extractedVehicleLicenseBackData.model || "" },
              { label: "رقم الشاصي", value: extractedVehicleLicenseBackData.chassisNumber || "" },
              { label: "رقم المحرك", value: extractedVehicleLicenseBackData.motorNumber || "" },
              { label: "عدد أسطوانات المحرك", value: extractedVehicleLicenseBackData.engineCylinders || "" },
              { label: "نوع الوقود", value: extractedVehicleLicenseBackData.fuelType || "" },
              { label: "سعة المحرك", value: extractedVehicleLicenseBackData.engineCapacity || "" },
              { label: "المميزات", value: extractedVehicleLicenseBackData.features || "" },
              { label: "الكود", value: extractedVehicleLicenseBackData.code || "" },
              { label: "سنة الصنع", value: extractedVehicleLicenseBackData.modelYear || "" },
              { label: "نوع الهيكل", value: extractedVehicleLicenseBackData.bodyStyle || "" },
              { label: "سنة انتهاء الفحص", value: extractedVehicleLicenseBackData.inspectionExpiryYear || "" },
              { label: "لون السيارة", value: extractedVehicleLicenseBackData.vehicleColor || "" },
            ];
            const extractedDataString = `"Make": "${extractedVehicleLicenseBackData.make || ""}", "Model": "${extractedVehicleLicenseBackData.model || ""}", "Chassis Number": "${extractedVehicleLicenseBackData.chassisNumber || ""}", "Motor Number": "${extractedVehicleLicenseBackData.motorNumber || ""}", "Engine Cylinders": "${extractedVehicleLicenseBackData.engineCylinders || ""}", "Fuel Type": "${extractedVehicleLicenseBackData.fuelType || ""}", "Engine Capacity": "${extractedVehicleLicenseBackData.engineCapacity || ""}", "Features": "${extractedVehicleLicenseBackData.features || ""}", "Code": "${extractedVehicleLicenseBackData.code || ""}", "Model Year": "${extractedVehicleLicenseBackData.modelYear || ""}", "Body Style": "${extractedVehicleLicenseBackData.bodyStyle || ""}", "Inspection Expiry Year": "${extractedVehicleLicenseBackData.inspectionExpiryYear || ""}", "Vehicle Color": "${extractedVehicleLicenseBackData.vehicleColor || ""}"`;

            if (onShowExtractedDataConfirmation) {
              onShowExtractedDataConfirmation(
                extractedDataFields,
                "تأكيد بيانات رخصة السيارة - الوجه الخلفي",
                "رخصة السيارة - الوجه الخلفي",
                (editedData) => {
                  const finalData = convertEditedFieldsToData(editedData, extractedVehicleLicenseBackData, "vehicle_license_back");
                  const dataString = createExtractedDataString(finalData, "vehicle_license_back");
                  const mismatchPayload = getVehicleMakeModelMismatchPayload(
                    finalData.make || "",
                    finalData.model || ""
                  );
                  if (mismatchPayload) {
                    send(
                      { text: systemUiMessage(VEHICLE_LICENSE_MAKE_MODEL_MISMATCH, mismatchPayload) },
                      true
                    );
                    if (setFlowStep) {
                      setFlowStep("idle");
                    }
                    return;
                  }
                  proceedWithExtractedData(dataString, USER_UPLOADED_VEHICLE_LICENSE_BACK);
                },
                fullBase64
              );
            } else {
              const mismatchPayload = getVehicleMakeModelMismatchPayload(
                extractedVehicleLicenseBackData.make || "",
                extractedVehicleLicenseBackData.model || ""
              );
              if (mismatchPayload) {
                send(
                  { text: systemUiMessage(VEHICLE_LICENSE_MAKE_MODEL_MISMATCH, mismatchPayload) },
                  true
                );
                if (setFlowStep) {
                  setFlowStep("idle");
                }
              } else {
                proceedWithExtractedData(extractedDataString, USER_UPLOADED_VEHICLE_LICENSE_BACK);
              }
            }
          } else {
            console.warn(
              `⚠️ [Ameen] Unknown flowStep when uploading image: ${flowStep}`
            );
          }
        } catch (error) {
          console.error(
            `❌ [Ameen] Error extracting document data from backend:`,
            error
          );
          hideLoading?.();

          // Check if this is a photo validation error (402 status code)
          const isValidationError = (error as any)?.isPhotoValidationError === true;
          const errorMessage = error instanceof Error ? error.message : String(error);
          const documentType = (error as any)?.documentType || flowStep;

          if (isValidationError) {
            // Send a specific message to AI about photo validation failure.
            // Do NOT reset flowStep here — keeping the current document step active
            // lets the user retry immediately without the camera closing or the
            // upload handler losing its document-step context.
            const photoValidationEvent = `${PHOTO_VALIDATION_FAILED_PREFIX}${flowStep}`;
            console.log(
              `📤 [Ameen] Sending [system_ui] ${photoValidationEvent} for ${flowStep}`
            );
            const payload = `"${errorMessage}". The uploaded photo is not clear or correct. Please ask the user to upload another clear photo of the ${documentType}.`;
            send(
              {
                text: systemUiMessage(photoValidationEvent, payload),
              },
              true
            );
          } else {
            // Still update state with image even if extraction fails (for non-validation errors)
            setCustomerNationalIdData((prev) => {
              return flowStep === "national_id_front"
                ? {
                  ...prev,
                  national_id_front_image_base64: base64,
                }
                : flowStep === "national_id_back"
                  ? {
                    ...prev,
                    national_id_back_image_base64: base64,
                  }
                  : prev;
            });

            // Send fallback message without extracted data if extraction failed
            const fallbackEvent = getDocumentUploadEventForFlowStep(flowStep);
            if (fallbackEvent) {
              console.log(
                `📤 [Ameen] Sending [system_ui] ${fallbackEvent} (extraction failed - non-validation)`
              );
              send({ text: systemUiMessage(fallbackEvent) }, true);
            }

            // ⚠️ Do NOT reset flowStep here.
            // Calling setFlowStep("idle") would break the retry path:
            // handleImagesUploaded checks flowStep to decide which extraction
            // API to call — if it's "idle", none of the branches match and
            // every subsequent upload is silently ignored.
            // The AI will move on via the fallback signal sent above.
          }
        } finally {
          hideLoading?.();
        }

        console.log(
          `📸 [Ameen] Document image (${flowStep}) processing complete:`,
          file.name
        );
      };

      reader.readAsDataURL(file);
    };


